import type { PreloadedEntryPoint } from 'react-relay';
import type { Route, RouterContext } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	defaultRegistry,
	createHook,
	type StoreActionApi,
} from '@atlassian/react-sweet-state';

type LastLoadedRoute = {
	route: Route;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	entryPointReference: null | PreloadedEntryPoint<any>;
};

type PrefetchingRoute = {
	routerContext: RouterContext;
	expiresAt: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	entryPointReference: null | PreloadedEntryPoint<any>;
};

export type State = {
	lastLoadedRoute: null | LastLoadedRoute;
	prefetchingRoute: null | PrefetchingRoute;
};

export const setLastLoadedRoute =
	({ route, entryPointReference }: LastLoadedRoute) =>
	({ setState }: StoreActionApi<State>) => {
		setState({
			lastLoadedRoute: {
				route,
				entryPointReference,
			},
		});
	};

export const setPrefetchingRoute =
	({ routerContext, entryPointReference, expiresAt }: PrefetchingRoute) =>
	({ setState }: StoreActionApi<State>) => {
		setState({
			prefetchingRoute: {
				routerContext,
				expiresAt,
				entryPointReference,
			},
		});
	};

const actions = {
	setLastLoadedRoute,
	setPrefetchingRoute,
};

type Actions = typeof actions;

const EntryPointsStore = createStore<State, Actions>({
	initialState: { lastLoadedRoute: null, prefetchingRoute: null },
	actions,
	name: 'react-resource-router/entry-points',
});

export const useLastLoadedRoute = createHook<State, Actions, LastLoadedRoute | null>(
	EntryPointsStore,
	{
		selector: (state) => state.lastLoadedRoute,
	},
);

export const getEntryPointsStore = () => defaultRegistry.getStore<State, Actions>(EntryPointsStore);

export const getEntryPointsState = () => getEntryPointsStore().storeState.getState();

export const getLastLoadedRoute = () => getEntryPointsState().lastLoadedRoute;
export const getPrefetchingRoute = () => getEntryPointsState().prefetchingRoute;
